'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "children", "disabled", "attachmentConfig"];
import * as React from 'react';
import useSlotProps from '@mui/utils/useSlotProps';
import { useChatComposer } from "../hooks/useChatComposer.js";
import { useChatStatus } from "../hooks/useChatStatus.js";
import { useChatStore } from "../hooks/useChatStore.js";
import { getDataAttributes } from "../internals/getDataAttributes.js";
import { ComposerContextProvider } from "./internals/ComposerContext.js";
import { jsx as _jsx } from "react/jsx-runtime";
export const ComposerRoot = /*#__PURE__*/React.forwardRef(function ComposerRoot(props, ref) {
  const {
      slots,
      slotProps,
      children,
      disabled = false,
      attachmentConfig
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const composer = useChatComposer();
  const status = useChatStatus();
  const store = useChatStore();
  const ownerState = React.useMemo(() => ({
    isSubmitting: composer.isSubmitting,
    hasValue: composer.value.trim() !== '',
    isStreaming: status.isStreaming,
    attachmentCount: composer.attachments.length,
    disabled
  }), [composer.attachments.length, composer.isSubmitting, composer.value, status.isStreaming, disabled]);
  const contextValue = React.useMemo(() => _extends({}, ownerState, {
    value: composer.value,
    setValue: composer.setValue,
    submit: composer.submit,
    addAttachment: composer.addAttachment,
    removeAttachment: composer.removeAttachment,
    attachments: composer.attachments,
    attachmentConfig,
    error: status.error,
    setComposerIsComposing: store.setComposerIsComposing
  }), [attachmentConfig, composer.addAttachment, composer.removeAttachment, composer.attachments, composer.setValue, composer.submit, composer.value, ownerState, status.error, store]);
  const Root = slots?.root ?? 'form';
  const rootProps = useSlotProps({
    elementType: Root,
    externalSlotProps: slotProps?.root,
    externalForwardedProps: other,
    ownerState,
    additionalProps: _extends({
      ref
    }, getDataAttributes({
      isSubmitting: ownerState.isSubmitting,
      hasValue: ownerState.hasValue,
      isStreaming: ownerState.isStreaming,
      disabled: ownerState.disabled
    }))
  });
  const externalOnSubmit = rootProps.onSubmit;
  return /*#__PURE__*/_jsx(ComposerContextProvider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(Root, _extends({}, rootProps, {
      onSubmit: event => {
        event.preventDefault();
        const originalPreventDefault = event.preventDefault.bind(event);
        let submitPreventedByExternal = false;
        event.preventDefault = () => {
          submitPreventedByExternal = true;
          originalPreventDefault();
        };
        externalOnSubmit?.(event);
        if (submitPreventedByExternal) {
          return;
        }
        if (disabled) {
          return;
        }
        void composer.submit();
      },
      children: children
    }))
  });
});
if (process.env.NODE_ENV !== "production") ComposerRoot.displayName = "ComposerRoot";